import { supabase } from "@/integrations/supabase/client";
import { MeterControlService } from "@/services/meterControlService";

export type LiveBalanceResult = {
  ok: boolean;
  balanceKwh: number | null;
  energyKwh?: number | null;
  readingAt: string | null;
  channel: "zhongyi" | "mqtt" | null;
  message?: string;
};

function isZhongyiMeter(meter: {
  deveui?: string | null;
  manufacturers?: { slug?: string | null } | null;
}): boolean {
  const slug = meter.manufacturers?.slug || null;
  if (slug === "zhongyi") return true;
  if (slug === "timewave") return false;
  return !!(meter.deveui && String(meter.deveui).trim());
}

/**
 * Ask the physical meter for a live balance (Zhongyi LoRaWAN or TimeWave 4G MQTT).
 * Does not clear existing UI metrics on failure — callers should keep last known values.
 */
export async function queryLiveMeterBalance(meterId: string): Promise<LiveBalanceResult> {
  const { data: meter, error: meterErr } = await supabase
    .from("smart_meters")
    .select("id, deveui, meter_type, manufacturers(slug)")
    .eq("id", meterId)
    .maybeSingle();

  if (meterErr || !meter) {
    return {
      ok: false,
      balanceKwh: null,
      readingAt: null,
      channel: null,
      message: meterErr?.message || "Meter not found",
    };
  }

  if (isZhongyiMeter(meter as any)) {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) {
      return {
        ok: false,
        balanceKwh: null,
        readingAt: null,
        channel: "zhongyi",
        message: "Not authenticated",
      };
    }

    const { data, error } = await supabase.functions.invoke("zhongyi-meter-control", {
      body: {
        meter_id: meterId,
        action: "query_balance",
        wait_ack_ms: 45000,
      },
      headers: { Authorization: `Bearer ${session.access_token}` },
    });

    if (error) {
      let detail = error.message || "Live query failed";
      try {
        const ctx = (error as any).context;
        if (ctx && typeof ctx.json === "function") {
          const body = await ctx.json();
          if (body?.error) detail = String(body.error);
        }
      } catch { /* keep default */ }
      return {
        ok: false,
        balanceKwh: null,
        readingAt: null,
        channel: "zhongyi",
        message: detail,
      };
    }

    if (data?.error || data?.success === false) {
      return {
        ok: false,
        balanceKwh: null,
        readingAt: null,
        channel: "zhongyi",
        message: data?.error || data?.summary || "Meter did not respond",
      };
    }

    const m = data?.metrics as {
      balance?: number | null;
      energy_kwh?: number | null;
      reading_time?: string | null;
    } | null | undefined;

    const balance =
      m?.balance != null && Number.isFinite(Number(m.balance))
        ? Number(m.balance)
        : null;

    if (balance == null && data?.ack_status !== "success") {
      return {
        ok: false,
        balanceKwh: null,
        readingAt: null,
        channel: "zhongyi",
        message: data?.summary || "No status uplink from meter (timeout)",
      };
    }

    return {
      ok: balance != null,
      balanceKwh: balance,
      energyKwh: m?.energy_kwh != null ? Number(m.energy_kwh) : null,
      readingAt: m?.reading_time || new Date().toISOString(),
      channel: "zhongyi",
      message: balance != null ? undefined : (data?.summary || "No balance in response"),
    };
  }

  // TimeWave / 4G MQTT
  const result = await MeterControlService.readMeter(meterId);
  if (!result.success || result.data?.electricityBalance == null) {
    return {
      ok: false,
      balanceKwh: null,
      readingAt: null,
      channel: "mqtt",
      message: result.message || "Meter did not respond",
    };
  }

  return {
    ok: true,
    balanceKwh: Number(result.data.electricityBalance),
    energyKwh:
      result.data.forwardEnergy != null
        ? Number(result.data.forwardEnergy)
        : result.data.combinedActiveTotal != null
          ? Number(result.data.combinedActiveTotal)
          : null,
    readingAt: new Date().toISOString(),
    channel: "mqtt",
  };
}
